import React, { useEffect, useRef, useState } from 'react';
import { MessageSquareIcon } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { trpc } from '../../lib/trpc';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { formatDate } from '../../utils/formatters';
import { isV2PreviewEnabled } from '../../utils/featureFlags';
import { PricingNoticeDialog, toIso } from '../pricing/PricingNoticeDialog';

/**
 * Navbar messages menu — distinct from the notifications bell (signals).
 * Messages are notices from Global Bridge to the brand. Today there is one:
 * the updated rate card. It used to be a blocking acknowledgment gate; it
 * is now a message with an unread badge (Ari, 2026-09-16). The badge count
 * is `required` (unread) from `pricingAcknowledgment.status`; users the
 * notice does not apply to (internal, exempt brands) get no icon at all.
 */
const MessagesMenu: React.FC = () => {
  const { isLoaded, isSignedIn, userId } = useAuth();
  if (!isLoaded || !isSignedIn || !userId) return null;
  return <SignedInMessagesMenu key={userId} clerkId={userId} />;
};

const SignedInMessagesMenu: React.FC<{ clerkId: string }> = ({ clerkId }) => {
  const { user } = useCurrentUser();
  const utils = trpc.useUtils();
  const input = { clerkId };
  const status = trpc.pricingAcknowledgment.status.useQuery(input, {
    // Wait for the matching profile so a prior session's cache never leaks.
    enabled: user?.clerkId === clerkId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
  const markRead = trpc.pricingAcknowledgment.accept.useMutation({
    onSuccess: (result) => {
      utils.pricingAcknowledgment.status.setData(input, (previous) =>
        previous ? { ...previous, required: false, readAt: result.acceptedAt } : previous,
      );
    },
  });
  const [open, setOpen] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const notice = status.data;
  // A stale profile from a prior session must not surface another user's notice.
  if (!notice || status.error || notice.exempt || user?.clerkId !== clerkId) return null;
  const unread = notice.required ? 1 : 0;
  // v2 shell users get the mock's top-bar control; v1 users keep today's icon (pinned by tests).
  const v2 = isV2PreviewEnabled(user ?? null);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        aria-label={unread > 0 ? `Messages (${unread} unread)` : 'Messages'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={
          v2
            ? 'relative grid h-8 w-8 place-items-center rounded-lg border border-line hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy'
            : 'flex items-center text-gray-600 focus:outline-none'
        }
      >
        {v2 ? (
          <>
            {/* v2: the mock's top-bar control (built-v12 `.bell`): 32px hairline square, 16px stroke icon. */}
            <svg data-testid="messages-v2-icon" viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-ink-soft" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
            {unread > 0 && (
              <span className="num absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-navy px-1 text-[10px] font-semibold text-white">
                {unread}
              </span>
            )}
          </>
        ) : (
          <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-800 relative">
            <MessageSquareIcon size={18} />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center num">
                {unread}
              </span>
            )}
          </div>
        )}
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Messages"
          className="absolute right-0 mt-2 w-80 rounded-xl border border-line bg-white shadow-lg z-20 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-line text-sm font-semibold text-ink">Messages</div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setShowNotice(true);
            }}
            className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3"
          >
            <span
              aria-hidden
              className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${notice.required ? 'bg-red-500' : 'bg-transparent'}`}
            />
            <span className="min-w-0">
              <span className={`block text-sm ${notice.required ? 'font-semibold text-ink' : 'text-ink'}`}>
                Updated fulfillment pricing
              </span>
              <span className="block text-xs text-ink-soft mt-0.5">
                Effective {notice.effectiveDate} ·{' '}
                {notice.readAt ? `Read ${formatDate(toIso(notice.readAt))}` : 'Unread'}
              </span>
            </span>
          </button>
        </div>
      )}
      {showNotice && (
        <PricingNoticeDialog
          notice={notice}
          readAt={notice.readAt}
          pending={markRead.isPending}
          error={!!markRead.error}
          onClose={() => setShowNotice(false)}
          onMarkRead={() =>
            markRead.mutate({ clerkId, version: notice.version, acknowledged: true })
          }
        />
      )}
    </div>
  );
};

export default MessagesMenu;
